import { ProFormSelect } from "@ant-design/pro-components";
import { Button, message, Divider, Input } from "antd";
import { useState, useMemo, useCallback } from "react";
import { getTimeToString } from "@/utils/toolt";
import { SearchOutlined } from "@ant-design/icons";
import { useAppSelector } from "@/store";
import { useTranslation } from "react-i18next";
import { TABLEPAI, USER, ACTIVE, SUPERUSER } from "@/enums/pageEnum";
import { debounce, isArray, isString } from "lodash-es";
import { UserAndOrganFilter, GetEamilInput } from "@/utils/commonConfig";
import {
  userSuspend,
  userInvite,
  removeUser,
  eidtUser,
  userResume,
  getGroupLists,
  findOrganRoles,
} from "@api";
import {
  PageTitle,
  ComfirmButton,
  CustomDropdown,
  BasicTable,
  CustomModal,
  Status,
} from "@/components";

export default function UserPage() {
  const { t } = useTranslation();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [refreshKey, setRefreshKey] = useState<number>();
  const [isShowGroup, setIsShowGroup] = useState<boolean>(false);
  const [options, setOptions] = useState<object[]>([]);
  const [optionsRoles, setOptionsRoles] = useState<object[]>([]);
  const { userName, role } = useAppSelector((state) => state.app);
  const [currentItem, setCurrentItem] = useState<any>({});

  const puspend = async (id: number | number[]) => {
    try {
      await userSuspend(id);
      setSelectedRowKeys([]);
      setRefreshKey(Math.random());
    } catch (error: any) {
      message.error(isString(error) ? error : error?.detail || "Error");
    }
  };

  const resume = async (id: number | number[]) => {
    await userResume(id);
    setRefreshKey(Math.random());
  };

  const getfindOrganRoles = useCallback(async () => {
    if (optionsRoles.length) return;
    const res = await findOrganRoles();
    const option = res.data.body?.map((item: string) => ({
      value: item,
      label: t(item),
    }));
    setOptionsRoles(option);
  }, [optionsRoles.length, t]);

  const getModalGroup = async (value: string) => {
    const res = await getGroupLists(value ? { name: value } : undefined);
    setOptions(res.data.body?.content || []);
  };

  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: string) => getModalGroup(value);

    return debounce(loadOptions, 800);
  }, []);

  const getSearchSelect = useCallback(() => {
    return (
      <ProFormSelect.SearchSelect
        name="groupIds"
        fieldProps={{
          labelInValue: false,
          showSearch: false,
          listHeight: 200,
          dropdownRender: (menu) => {
            return (
              <>
                {menu}
                <Divider className="mb-0 mt-1" />
                <Input
                  placeholder="Group name"
                  className="w-full my-1.5"
                  allowClear
                  suffix={<SearchOutlined />}
                  onChange={(val: any) => {
                    debounceFetcher(val.target.value);
                  }}
                  onKeyDown={(e: any) => e.stopPropagation()}
                />
              </>
            );
          },
          fieldNames: { label: "name", value: "id" },
        }}
        options={options}
        label={t("group")}
      />
    );
  }, [debounceFetcher, t, options]);

  const getRoleCom = useCallback(() => {
    return (
      <ProFormSelect
        name="role"
        options={optionsRoles}
        onChange={(val) => {
          setIsShowGroup(val === USER);
        }}
        label={t("role")}
        rules={[{ required: true }]}
      />
    );
  }, [optionsRoles, t]);

  const getInitValues = useMemo(() => {
    const { groups, role } = currentItem;
    return {
      role,
      groupIds: groups && isArray(groups) ? groups.map((item) => item.id) : [],
    };
  }, [currentItem]);

  const getModalEdit = useCallback(
    (record: any) => {
      const { nickname, id, role } = record;
      return (
        <CustomModal
          title={t("edit")}
          request={eidtUser}
          otherParams={{ id }}
          initValues={getInitValues}
          onShowModal={() => {
            setCurrentItem(record);
            setIsShowGroup(role === USER);
            getModalGroup("");
            getfindOrganRoles();
          }}
          onClick={() => setRefreshKey(Math.random())}
          onColseModal={() => setIsShowGroup(false)}
          buttonCogfig={{ variant: "link", className: "pl-0 pr-0" }}
        >
          <p className="pb-5 pl-[114px]">
            <span className="text-[#FF1212] text-[16px] float-left mt-1 mr-1">
              *{" "}
            </span>
            {t("userName")}: <span className="pl-3">{nickname}</span>
          </p>
          {getRoleCom()}
          {isShowGroup && getSearchSelect()}
          <p className="pb-8 pl-[126px]">
            {t("password")}: <span className="pl-3">******</span>
          </p>
        </CustomModal>
      );
    },
    [
      getRoleCom,
      getSearchSelect,
      isShowGroup,
      t,
      getfindOrganRoles,
      getInitValues,
    ]
  );

  const columns: any[] = useMemo(() => {
    const publicCols = [
      {
        title: t("role"),
        dataIndex: "role",
        render: (text: string) => <>{t(text)}</>,
      },
      {
        title: t("lastActivity"),
        dataIndex: "lastActiveAt",
        render: (text: number | null) => <>{getTimeToString(text)}</>,
      },
      {
        title: t("status"),
        dataIndex: "status",
        render: (text: string) => <Status status={text} />,
      },
      {
        title: t("action"),
        dataIndex: "action",
        render: (
          _: unknown,
          record: {
            nickname: string;
            id: number;
            status: string;
            role: string;
            groups: { id: number; name: string }[];
          }
        ) => {
          const { nickname, id, status } = record;
          if (nickname === userName) return "-";
          return (
            <>
              {getModalEdit(record)}
              <CustomDropdown
                dropdownRender={() => (
                  <div className="bg-[#fff] dark:bg-[#141414] cgboxShadow flex flex-col justify-center items-center">
                    <div>
                      <Button
                        color="default"
                        variant="link"
                        onClick={() => {
                          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                          status === ACTIVE ? puspend([id]) : resume([id]);
                        }}
                      >
                        {status === ACTIVE ? t("suspend") : t("resume")}
                      </Button>
                    </div>
                    <div>
                      <ComfirmButton
                        buttonCogfig={{
                          className: "#000",
                          variant: "link",
                        }}
                        onClick={() => {
                          removeUser([id]).then(() => {
                            setRefreshKey(Math.random());
                          });
                        }}
                        title={t("remove")}
                      />
                    </div>
                  </div>
                )}
              />
            </>
          );
        },
      },
    ];
    if (role === SUPERUSER) {
      return [{ title: t("userName"), dataIndex: "nickname" }, ...publicCols];
    }
    return [
      { title: t("userName"), dataIndex: "nickname" },
      {
        title: t("group"),
        dataIndex: "groups",
        render: (groups: unknown) => {
          return isArray(groups)
            ? groups?.map((item) => item?.name).join(", ")
            : groups;
        },
      },
      ...publicCols,
    ];
  }, [t, userName, role, getModalEdit]);
  return (
    <div className="p-4">
      <PageTitle title={t("user_management")} />
      <BasicTable
        columns={columns}
        key={refreshKey}
        tableHeader={() => (
          <>
            <CustomModal
              title={t("inviteMember")}
              onShowModal={() => {
                getModalGroup("");
                getfindOrganRoles();
              }}
              request={userInvite}
              onColseModal={() => setIsShowGroup(false)}
            >
              {GetEamilInput(t("email"))}
              {getRoleCom()}
              {isShowGroup && getSearchSelect()}
              <div className="text-[#666] pb-8 pl-[150px]">
                {t("hint.userTIshi")}
              </div>
            </CustomModal>
            <Button
              type="default"
              className="ml-5"
              onClick={() => {
                puspend(selectedRowKeys as number[]);
              }}
              disabled={!selectedRowKeys.length}
            >
              {t("suspend")}
            </Button>
          </>
        )}
        filterTableFields={UserAndOrganFilter(t("userName"), t("status"))}
        headerTitle={() => t("userList")}
        isDefaultToolBar={false}
        requestUrl={TABLEPAI.USER}
        onChangeSelected={(rowkeys) => setSelectedRowKeys(rowkeys)}
      />
    </div>
  );
}
